export const imageSchema = {
  type: 'object',
  properties: {
    preview: { type: 'string' },
    height: { type: 'number' },
    width: { type: 'number' },
    src: { type: 'string' },
    aspect: { type: ['number', 'null'] },
    originalSrc: { type: 'string' },
  },
  additionalProperties: false,
  required: ['src'],
};

export const questionHasCorrectAnswer = {
  hasCorrectAnswer: { type: 'boolean' },
};

export const questionHasAward = {
  hasAward: { type: 'boolean' },
};

export const questionAutoCommit = {
  autoCommit: { type: 'boolean' },
};

export const questionAutoNext = {
  autoNext: { type: 'boolean' },
};

export const questionHasOthers = {
  hasOthers: { type: 'boolean' },
  othersLimit: { type: 'number' },
  othersLabel: { type: 'string' },
  othersRequired: { type: 'boolean' },
  othersPlaceholder: { type: 'string' },
  othersReplaceUnsafe: { type: 'boolean' },
};

export const questionSequentialDisplay = {
  hasSequentialDisplay: { type: 'boolean' },
} as const;

export const questionHasTimer = {
  hasTimer: { type: 'boolean' },
  timerStrict: { type: 'boolean' },
  timerDuration: { type: 'number' },
  timerExpiredText: { type: 'string' },
} as const;

export const questionRandomizeGroup = {
  randomizeGroup: { type: 'boolean' },
  randomizeGroupExcludedIds: {
    items: { type: 'string' },
    type: 'array',
  },
} as const;

export const questionAwardAmount = {
  awardAmount: { type: 'number', description: 'Вознаграждение, целое число' },
};

export const questionFixedRandomizeAnswers = {
  fixedRandomizeAnswers: {
    type: 'object',
    properties: {
      questionId: { type: 'string' },
    },
    required: ['questionId'],
  },
};

export const questionRandomizeAnswers = {
  randomizeAnswers: { type: 'boolean' },
};

export const questionRequiresAnswer = {
  requiresAnswer: { type: 'boolean' },
};

export const questionText = {
  textLabel: { type: 'string' },
  textLimit: { type: 'number' },
  textReplaceUnsafe: { type: 'boolean' },
};

export const questionHasNotApplicable = {
  hasNotApplicable: { type: 'boolean' },
  notApplicableLabel: { type: 'string' },
};

export const TEXT_OPERATIONS = ['TEXT_EQUALS', 'TEXT_NOT_EQUALS', 'TEXT_CONTAINS', 'TEXT_NOT_CONTAINS'] as const;
export const ANSWER_OPERATIONS = [
  'EQUALS',
  'NOT_EQUALS',
  'EQUALS_ANY_OF',
  'NOT_EQUALS_ANY_OF',
  'ANY_OF',
  'ALL_OF',
  'NOT_ANY_OF',
  'NOT_ALL_OF',
] as const;
export const RANK_OPERATIONS = ['RANK_EQUALS', 'RANK_GREATER', 'RANK_LESS'] as const;
export const INSTANT_STATISTICS_OPERATIONS = ['GREATER_OR_EQUALS', 'GREATER', 'LESS_OR_EQUALS', 'LESS'];
/** Операторы для атрибутов conjoint */
export const CONJOINT_ATTRIBUT_OPERATIONS = [
  /** На всех экранах выбрали вариант */
  'ALL_OF_CONJOINT',
  /** На любом экране выбрали вариант */
  'ANY_OF_CONJOINT',
  /** Выбрали не на всех экранах */
  'NOT_ALL_OF_CONJOINT',
  /** Не выбрали вообще */
  'NOT_ANY_OF_CONJOINT',
];
export const CONJOINT_ATTRIBUT_OPERATIONS_WITH_COUNT = [
  /** На x экранах выбрали вариант */
  'SOME_OF_CONJOINT',
  /** На x экранах не выбрали вариант */
  'NOT_SOME_OF_CONJOINT',
];
export const NUMBER_OPERATIONS = [
  'MATH_EQUALS',
  'MATH_NOT_EQUALS',
  'GREATER_OR_EQUALS',
  'GREATER',
  'LESS_OR_EQUALS',
  'LESS',
  'ANSWER_COUNT_EQUALS',
  'ANSWER_COUNT_GREATER',
  'ANSWER_COUNT_LESS',
] as const;

const TIME_OPERATIONS = ['TIME_EXPIRED', 'TIME_NOT_EXPIRED'] as const;

export const AWARD_COUNT_OPERATIONS = ['AWARD_EQUALS', 'AWARD_COUNT_LESS', 'AWARD_COUNT_GREATER'];
export const ANSWER_CORRECT_OPERATIONS = ['ANSWER_IS_CORRECT', 'ANSWER_NOT_CORRECT'];

export const SEGMENT_EXISTENCE_OPERATIONS = ['EXISTS', 'NOT_EXISTS'];
export const SEGMENT_PARAMETER_OPERATIONS = ['EQUALS', 'NOT_EQUALS'];
export const SEGMENT_PARAMETER_LIST_OPERATIONS = ['ANY_OF', 'NOT_ANY_OF'];

export const ENRICH_PARAMETER_OPERATIONS = ['EQUALS', 'NOT_EQUALS'];
export const ENRICH_PARAMETER_LIST_OPERATIONS = ['ANY_OF', 'NOT_ANY_OF'];

export const QUERY_STRING_OPERATIONS = ['ANY_OF', 'NOT_ANY_OF'];

export const SCORE_TYPES = ['CORRECT_ANSWER_COUNT', 'CORRECT_QUESTION_COUNT', 'SCORE_NUMBER'] as const;
export const SCORE_OPERATIONS = [
  'MATH_EQUALS',
  'MATH_NOT_EQUALS',
  'GREATER_OR_EQUALS',
  'GREATER',
  'LESS_OR_EQUALS',
  'LESS',
] as const;

export const answerImage = {
  image: imageSchema,
};

export const answerImageBackground = {
  imageBackground: { type: 'string' },
};

export const answerAwardAmount = {
  awardAmount: { type: 'number', description: 'Вознаграждение, целое число' },
};

export function createBasicAnswerSchema(properties = {}) {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      text: { type: 'string' },
      ...properties,
    },
    additionalProperties: false,
    required: ['id', 'isCorrect'],
  };
}

export const setRandomizePages = {
  randomizeSet: { type: 'boolean' },
  randomizeSetExcludedIds: {
    items: { type: 'string' },
    type: 'array',
  },
} as const;

export const instantStatisticRequired = {
  instantStatisticRequired: { type: 'boolean' },
} as const;

export const hasGlobalTimer = {
  hasTimer: { type: 'boolean' },
  timerStrict: { type: 'boolean' },
  timerDuration: { type: 'number', description: 'Интервал таймера в секундах' },
  timerExpiredText: { type: 'string' },
} as const;

export const topLevelLogicRules = {
  logicOperationType: {
    enum: ['OR'],
  },
  logicRules: {
    type: 'array',
    items: {
      $ref: '#/definitions/logicContainer',
    },
  },
} as const;

export const logicRulesSchemas = [
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: ANSWER_OPERATIONS.filter(o => o !== 'NOT_ANY_OF') },
      answerIds: { type: 'array', items: { type: 'string' } },
    },
    required: ['id', 'source', 'questionId', 'operation', 'answerIds'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      // исключение для безусловных переходов, которые используются для не-последних в массиве, последних по флагу экранов прототипа
      operation: { enum: ['NOT_ANY_OF'] },
      answerIds: { type: 'array', items: { type: 'string' } },
    },
    required: ['id', 'source', 'questionId', 'operation', 'answerIds'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: RANK_OPERATIONS },
      number: { type: 'number' },
      answerIds: { type: 'array', items: { type: 'string' }, minItems: 1 },
    },
    required: ['id', 'source', 'questionId', 'number', 'operation', 'answerIds'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: TEXT_OPERATIONS },
      others: { type: 'string' },
    },
    required: ['id', 'source', 'questionId', 'others', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: ['TEXT_FILLED', 'TEXT_NOT_FILLED'] },
    },
    required: ['id', 'source', 'questionId', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: TIME_OPERATIONS },
    },
    required: ['id', 'source', 'questionId', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: CONJOINT_ATTRIBUT_OPERATIONS },
      levelId: { type: 'string' },
    },
    required: ['id', 'source', 'questionId', 'levelId', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: CONJOINT_ATTRIBUT_OPERATIONS_WITH_COUNT },
      levelId: { type: 'string' },
      count: { type: 'number' },
    },
    required: ['id', 'source', 'count', 'questionId', 'levelId', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'stats' },
      operation: { enum: INSTANT_STATISTICS_OPERATIONS },
      value: { type: 'number' },
    },
    anyOf: [
      {
        properties: {
          setId: { type: 'string' },
        },
        required: ['setId'],
      },
      {
        properties: {
          groupId: { type: 'string' },
        },
        required: ['groupId'],
      },
      {
        properties: {
          questionId: { type: 'string' },
        },
        required: ['questionId'],
      },
      {
        properties: {
          questionId: { type: 'string' },
          answerId: { type: 'string' },
        },
        required: ['answerId', 'questionId'],
      },
    ],
    additionalProperties: true,
    unevaluatedProperties: false,
    required: ['id', 'source', 'value', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: NUMBER_OPERATIONS },
      number: { type: 'number' },
    },
    required: ['id', 'source', 'questionId', 'number', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: AWARD_COUNT_OPERATIONS },
      number: { type: 'number' },
    },
    required: ['id', 'source', 'questionId', 'number', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'answers' },
      questionId: { type: 'string' },
      operation: { enum: ANSWER_CORRECT_OPERATIONS },
    },
    required: ['id', 'source', 'questionId', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'score' },
      type: { enum: SCORE_TYPES },
      operation: { enum: SCORE_OPERATIONS },
      number: { type: 'number' },
    },
    additionalProperties: false,
    required: ['id', 'source', 'number', 'operation', 'type'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'segment' },
      segmentId: { type: 'string' },
      operation: { enum: SEGMENT_EXISTENCE_OPERATIONS },
    },
    required: ['id', 'source', 'segmentId', 'operation'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'segment' },
      segmentId: { type: 'string' },
      name: { type: 'string' },
      operation: { enum: SEGMENT_PARAMETER_OPERATIONS },
      value: { type: ['string', 'number'] },
    },
    required: ['id', 'source', 'segmentId', 'name', 'operation', 'value'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'segment' },
      segmentId: { type: 'string' },
      name: { type: 'string' },
      operation: { enum: SEGMENT_PARAMETER_LIST_OPERATIONS },
      value: {
        oneOf: [
          { type: 'array', items: { type: 'string' } },
          { type: 'array', items: { type: 'number' } },
        ],
      },
    },
    required: ['id', 'source', 'segmentId', 'name', 'operation', 'value'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'enrich' },
      name: { type: 'string' },
      operation: { enum: ENRICH_PARAMETER_OPERATIONS },
      value: { type: 'string' },
    },
    required: ['id', 'source', 'name', 'operation', 'value'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'enrich' },
      name: { type: 'string' },
      operation: { enum: ENRICH_PARAMETER_LIST_OPERATIONS },
      value: { type: 'array', items: { type: 'string' } },
    },
    required: ['id', 'source', 'name', 'operation', 'value'],
  },
  {
    properties: {
      id: { type: 'string' },
      source: { const: 'qs' },
      name: { type: 'string' },
      operation: { enum: QUERY_STRING_OPERATIONS },
      value: { type: 'array', items: { type: 'string' } },
    },
    required: ['id', 'source', 'name', 'operation', 'value'],
  },
] as const;

export const logicRuleDefinition = {
  logicRule: {
    type: 'object',
    oneOf: logicRulesSchemas.map(v => ({ additionalProperties: false, ...v })),
  },
} as const;

export const logicContainerDefinition = {
  logicContainer: {
    type: 'object',
    additionalProperties: false,
    properties: {
      logicOperationType: {
        enum: ['AND'],
      },
      logicRules: {
        type: 'array',

        items: {
          type: 'object',
          unevaluatedProperties: false,
          allOf: [
            {
              $ref: '#/definitions/logicRule',
            },
          ],
        },
      },
    },
    required: ['logicRules', 'logicOperationType'],
  },
  // strictLogicRule: {
  //   allOf: [{ $ref: '#/definitions/logicRule' }],
  //   unevaluatedProperties: false,
  // },
} as const;

export const universalLink = {
  oneOf: [
    { type: 'string' },
    {
      type: 'object',
      properties: { webUrl: { type: 'string' }, webviewUrl: { type: 'string' } },
      required: ['webUrl', 'webviewUrl'],
    },
  ],
} as const;
